import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getGetAllJobsQueryKey, useGetJobStatus, useRunJobInEngine } from '@/common/api/endpoints/jobs/jobs';
import { useDialog } from '@/common/context/DialogContext';
import log from '@/common/utils/logger';
import { useModuleJobStore } from './useModuleJobStore';

interface UseModuleRubricJobParams {
  moduleId: string | null | undefined;
  version: string | null | undefined;
  tasksQueryKey: readonly unknown[];
}

interface StoredJobStatus {
  id: string;
  status: string;
  currentStage?: string;
}

/**
 * Orchestrates bulk rubric generation for a module via the job engine.
 * Locks the module editor while the job is active and refetches tasks on completion.
 */
export const useModuleRubricJob = ({
  moduleId,
  version,
  tasksQueryKey,
}: UseModuleRubricJobParams) => {
  const { t } = useTranslation();
  const { showToastMessage } = useDialog();
  const queryClient = useQueryClient();
  const runJobMutation = useRunJobInEngine();

  const storedJobId = useModuleJobStore((state) => (
    moduleId ? state.moduleJobs[moduleId] : undefined
  ));
  const setModuleJob = useModuleJobStore((state) => state.setModuleJob);
  const clearModuleJob = useModuleJobStore((state) => state.clearModuleJob);

  const completionHandledRef = useRef<string | null>(null);

  const { data: trackedJob } = useGetJobStatus(storedJobId ?? null, undefined, {
    query: {
      enabled: !!storedJobId,
      refetchInterval: (query) => {
        const jobs = query.state.data?.data?.data as StoredJobStatus[] | undefined;
        const job = jobs?.[0];
        if (!job) return 5000;
        if (job.status === 'running' || job.status === 'pending') return 5000;
        return false;
      },
      select: (response) => {
        const jobs = response.data?.data as unknown as StoredJobStatus[] | undefined;
        return jobs?.[0];
      },
    },
  });

  const isJobActive = !!storedJobId && (
    !trackedJob
    || trackedJob.status === 'running'
    || trackedJob.status === 'pending'
  );

  useEffect(() => {
    if (!moduleId || !storedJobId || !trackedJob) return;
    if (trackedJob.status !== 'success' && trackedJob.status !== 'failed') return;
    if (completionHandledRef.current === storedJobId) return;

    completionHandledRef.current = storedJobId;
    clearModuleJob(moduleId);
    queryClient.invalidateQueries({ queryKey: tasksQueryKey });

    if (trackedJob.status === 'success') {
      showToastMessage(
        'success',
        t('lecturer.moduleEditor.moduleEditor.messages.generateAllRubricsSuccess'),
        t('lecturer.moduleEditor.moduleEditor.buttons.generateAllRubrics'),
      );
    } else {
      showToastMessage(
        'error',
        trackedJob.currentStage || t('lecturer.moduleEditor.moduleEditor.messages.generateAllRubricsFailure'),
        t('lecturer.moduleEditor.moduleEditor.buttons.generateAllRubrics'),
      );
    }
  }, [
    moduleId,
    storedJobId,
    trackedJob,
    clearModuleJob,
    queryClient,
    tasksQueryKey,
    showToastMessage,
    t,
  ]);

  const launch = useCallback(async (eligibleTaskIds: string[], label: string) => {
    if (!moduleId || !version) return;
    if (eligibleTaskIds.length === 0) {
      showToastMessage(
        'info',
        t('lecturer.moduleEditor.moduleEditor.messages.generateAllRubricsNothingToDo'),
        t('lecturer.moduleEditor.moduleEditor.buttons.generateAllRubrics'),
      );
      return;
    }
    if (isJobActive) return;

    try {
      const response = await runJobMutation.mutateAsync({
        data: {
          type: 'bulk',
          label,
          params: {
            operations: ['generate_task_rubric_with_objectives'],
            entityIds: eligibleTaskIds,
            options: {
              generate_task_rubric_with_objectives: {
                module_id: moduleId,
                version,
                save_result: true,
              },
            },
          },
        },
      });

      const jobId = (response?.data?.data as { jobId?: string } | undefined)?.jobId;
      if (!jobId) {
        showToastMessage(
          'error',
          t('lecturer.moduleEditor.moduleEditor.messages.generateAllRubricsFailure'),
          t('lecturer.moduleEditor.moduleEditor.buttons.generateAllRubrics'),
        );
        return;
      }

      completionHandledRef.current = null;
      setModuleJob(moduleId, jobId);
      // Refetch the job tray immediately so it appears without waiting for the poll interval.
      queryClient.invalidateQueries({ queryKey: getGetAllJobsQueryKey({}) });
    } catch (error) {
      log.error(error);
      showToastMessage(
        'error',
        t('lecturer.moduleEditor.moduleEditor.messages.generateAllRubricsFailure'),
        t('lecturer.moduleEditor.moduleEditor.messages.generateAllRubricsFailure'),
      );
    }
  }, [
    moduleId,
    version,
    isJobActive,
    runJobMutation,
    setModuleJob,
    queryClient,
    showToastMessage,
    t,
  ]);

  return {
    launch,
    isJobActive,
    isLaunching: runJobMutation.isPending,
  };
};
